import React, { useState, useEffect, memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@material-ui/core/styles'

import { useSnackbar } from 'notistack'

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  CircularProgress,
} from '@material-ui/core'

import {
  CloseRounded as CloseIcon,
  BackspaceOutlined as EraserRounded,
} from '@material-ui/icons'

import IconButton from '../../atoms/inputs/iconButton'
import TextField from '../../atoms/inputs/textfield'
import api from '../../../services/api'
import messages from '../../../services/messages'
import SelectField from '../../atoms/inputs/selectfield'

const CreateAttendanceComponent = (props) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()

  const [form, setForm] = useState({
    name: null,
    cpf: '',
    skill: null,
  })
  const [formError, setFormError] = useState([])
  const [skills, setSkills] = useState([])
  const [status, setStatus] = useState({
    get: {
      loading: false,
      error: false,
      success: false,
    },
    create: {
      loading: false,
      error: false,
      success: false,
    },
  })

  const disableCreateButton = useMemo(() => {
    return (
      Object.values(form).length < 3 ||
      status.create.loading ||
      status.get.loading ||
      Object.values(formError)?.some((el) => el) ||
      Object.values(form)?.some((el) => el === '' || el === null)
    )
  }, [status.create.loading, status.get.loading, form, formError])

  const handleRefresh = async () => {
    if (status?.get?.loading) return
    setStatus((prev) => ({
      ...prev,
      get: { loading: true, error: false, success: false },
    }))
    try {
      const response = await api.get(`/skillBySource?source=telefone`)
      if (response.status === 200) {
        setSkills(
          response.data.data.skills?.map((skill) => ({
            _id: skill.id,
            name: skill.name,
          }))
        )
        setStatus((prev) => ({
          ...prev,
          get: { loading: false, error: false, success: true },
        }))
      } else {
        throw response
      }
    } catch (error) {
      const message = t('toasts.genericError')
      
      setStatus((prev) => ({
        ...prev,
        get: { loading: false, error: message, success: false },
      }))
    }
  }

  function validateForms() {
    Object.keys(form).forEach((key) => {
      if (form[key] === '') {
        setFormError((prev) => ({
          ...prev,
          [key]: `${key} is required`,
        }))
      } else {
        setFormError((prev) => ({
          ...prev,
          [key]: false,
        }))
      }
    })

    if (form.cpf && !validateCPF(form.cpf)) {
      setFormError((prev) => ({
        ...prev,
        cpf: `cpf is invalid`,
      }))
    } else {
      setFormError((prev) => ({
        ...prev,
        cpf: false,
      }))
    }

    if (form.name && form.name.length < 4) {
      setFormError((prev) => ({
        ...prev,
        name: `name min length`,
      }))
    } else if (form.name && form.name.length > 50) {
      setFormError((prev) => ({
        ...prev,
        name: `name max length`,
      }))
    }
  }

  function validateCPF(cpf) {
    cpf = cpf.replace(/\D/g, '')
    if (cpf.toString().length != 11 || /^(\d)\1{10}$/.test(cpf)) return false
    let result = true
    ;[9, 10].forEach(function (j) {
      var soma = 0,
        r
      cpf
        .split(/(?=)/)
        .splice(0, j)
        .forEach(function (e, i) {
          soma += parseInt(e) * (j + 2 - (i + 1))
        })
      r = soma % 11
      r = r < 2 ? 0 : 11 - r
      if (r != cpf.substring(j, j + 1)) result = false
    })
    return result
  }

  const cpfMask = (value) => {
    return value
      ?.replace(/\D/g, '')
      ?.replace(/(\d{3})(\d)/, '$1.$2')
      ?.replace(/(\d{3})(\d)/, '$1.$2')
      ?.replace(/(\d{3})(\d{1,2})/, '$1-$2')
      ?.replace(/(-\d{2})\d+?$/, '$1')
  }

  const inputHandle = (event, value) => {
    setForm((prev) => ({
      ...prev,
      [event?.target?.name]: value || event?.target?.value,
    }))
  }

  const handleCreate = async (data) => {
    if (status.create.loading) return
    setStatus((prev) => ({
      ...prev,
      create: { loading: true, error: false, success: false },
    }))
    try {
      const response = await messages.post(
        `/channel/phone/receive/${data.skill}`,
        {
          ...data,
          cpf: data.cpf.replace(/\D/g, ''),
        }
      )
      if (response.status >= 200 && response.status < 300) {
        setStatus((prev) => ({
          ...prev,
          create: { loading: false, error: false, success: true },
        }))
        setForm({
          name: null,
          cpf: '',
          skill: null,
        })
        setFormError({})
        props.setDialogOpen(false)
      } else {
        throw response
      }
    } catch (error) {
      const message = t('toasts.genericError')
      setStatus((prev) => ({
        ...prev,
        create: { loading: false, error: message, success: false },
      }))
    }
  }

  useEffect(() => {
    if (status.get.error) {
      enqueueSnackbar(t(status.get.error), {
        variant: 'error',
        autoHideDuration: 3000,
      })
    }

    if (status.create.error) {
      enqueueSnackbar(t(status.create.error), {
        variant: 'error',
        autoHideDuration: 3000,
      })
    }

    if (status.create.success) {
      enqueueSnackbar(t('toasts.createAttendanceSuccess'), {
        variant: 'success',
        autoHideDuration: 3000,
      })
    }
  }, [status.get.error, status.create.error, status.create.success])

  useEffect(() => {
    validateForms()
  }, [form])

  useEffect(() => {
    if (!props.dialogOpen) return
    if (status.get.success) return
    handleRefresh()
  }, [props?.dialogOpen])

  useEffect(() => {
    if (!props.dialogOpen) return
    setFormError({})
  }, [props?.dialogOpen])

  useEffect(() => {
    if (!props.dialogOpen) return
    if (skills?.length === 1) {
      setForm({
        ...form,
        skill: skills[0]._id,
      })
    }
  }, [skills, props?.dialogOpen])

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="xs"
        open={props.dialogOpen || status.create.loading}
        onClick={(e) => {
          e.stopPropagation()
        }}
        onClose={(e) => {
          e.stopPropagation()
          props.setDialogOpen(false)
        }}
        aria-labelledby="form-dialog-title"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            handleCreate(form)
          }}
        >
          <DialogTitle id="form-dialog-title">
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>{t('commons.createAttendance')}</div>
              <div>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation()
                    setForm((prev) => ({
                      ...prev,
                      name: null,
                      cpf: "",
                    }))
                  }}
                  style={{ color: theme.palette.primary.main }}
                >
                  <EraserRounded fontSize="small" />
                </IconButton>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation()
                    props.setDialogOpen(false)
                  }}
                  style={{ color: theme.palette.primary.main }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            </div>
          </DialogTitle>
          <DialogContent>
            <TextField
              id="field-Name"
              label={t('fields.name.label')}
              placeholder={t('fields.name.placeHolder')}
              type="text"
              color="primary"
              variant="outlined"
              name="name"
              value={form.name ? form.name : ''}
              onChange={inputHandle}
              error={formError?.name ? true : false}
              helperText={
                formError?.name && t(`fields.name.errors.${formError.name}`)
              }
              shrink={true}
              fullWidth={true}
              autoComplete="off"
            />
            <div style={{ marginTop: '1rem' }} />
            <TextField
              id="field-cpf"
              label={t('fields.cpf.label')}
              placeholder={t('fields.cpf.placeHolder')}
              type="cpf"
              color="primary"
              variant="outlined"
              name="cpf"
              value={form.cpf}
              onChange={(e) => {
                e.target.value = e.target.value?.replace(/\D/g, '')
                inputHandle(e, cpfMask(e?.target?.value))
              }}
              error={formError?.cpf ? true : false}
              helperText={
                formError?.cpf && t(`fields.cpf.errors.${formError.cpf}`)
              }
              shrink={true}
              fullWidth={true}
              autoComplete="off"
            />
            <div style={{ marginTop: '1rem' }} />
            <SelectField
              name={'skill'}
              label={t('fields.skill.label')}
              data={skills}
              value={form.skill}
              placeholder={t('fields.skill.placeHolder')}
              onChange={(e) => {
                inputHandle(e)
              }}
              disabled={status.get.loading}
              defaultValue={form.skill}
              disadisableAutoselect
              isRefDocument
            />
          </DialogContent>
          <DialogActions>
            <div
              style={{
                display: 'flex',
                width: '100%',
                justifyContent: 'space-between',
              }}
            >
              <div>
                <Button
                  size="large"
                  color="primary"
                  onClick={(e) => {
                    e.stopPropagation()
                    props.setDialogOpen(false)
                  }}
                >
                  {t('buttons.cancel')}
                </Button>
                <Button
                  size="large"
                  variant="contained"
                  type="submit"
                  color="primary"
                  disabled={disableCreateButton}
                  style={{ marginLeft: 5 }}
                >
                  {status.create.loading || status.get.loading ? (
                    <CircularProgress size={25} color="primary" />
                  ) : (
                    t('buttons.confirm')
                  )}
                </Button>
              </div>
            </div>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
}

export default memo(CreateAttendanceComponent)
